// BaseRoleService.js

import { doc, getDoc, onSnapshot } from 'firebase/firestore';

import { collections, db } from 'firebaseConfig';
import {
  canAccessAction,
  canRole,
  isBuiltinRole,
  normalizeRole,
  resolveMemberAccess,
  BASE_ROLES,
} from 'security/rbac';
import { subscribeToBaseCustomRole } from 'services/bases/BaseCustomRoleService';

function baseDocRef(baseKey) {
  return doc(db, collections.bases, baseKey);
}

function baseMemberDocRef(baseKey, userKey) {
  return doc(db, collections.bases, baseKey, 'members', userKey);
}

function inferAccessFromRows(baseRow, memberRow, userKey) {
  if (!baseRow || !userKey) return null;
  return resolveMemberAccess({
    ownerKey: baseRow.ownerKey,
    userKey,
    memberRole: memberRow?.role,
    memberBaseRole: memberRow?.baseRole,
  });
}

export function canBaseRole(role, action, options = {}) {
  if (options.isCustom || options.roleDefinition || options.roleMissing) {
    return canAccessAction({
      tier: options.tier || BASE_ROLES.MEMBER,
      isCustom: options.isCustom === true,
      roleDefinition: options.roleDefinition || null,
      roleMissing: options.roleMissing === true,
      action,
    });
  }
  return canRole(role, action);
}

export async function getBaseRoleForUser({ baseKey, userKey, base }) {
  if (!baseKey || !userKey) return null;

  const baseRow = base || (await getDoc(baseDocRef(baseKey))).data() || null;
  if (!baseRow) return null;
  if (baseRow.ownerKey === userKey) return BASE_ROLES.OWNER;

  const memberSnap = await getDoc(baseMemberDocRef(baseKey, userKey));
  if (!memberSnap.exists()) return null;

  const access = resolveMemberAccess({
    ownerKey: baseRow.ownerKey,
    userKey,
    memberRole: memberSnap.data()?.role,
    memberBaseRole: memberSnap.data()?.baseRole,
  });
  return access?.roleKey || null;
}

export async function getBaseAccessForUser({ baseKey, userKey, base }) {
  if (!baseKey || !userKey) return null;

  const baseRow = base || (await getDoc(baseDocRef(baseKey))).data() || null;
  if (!baseRow) return null;

  if (baseRow.ownerKey === userKey) {
    return {
      roleKey: BASE_ROLES.OWNER,
      tier: BASE_ROLES.OWNER,
      isCustom: false,
      roleDefinition: null,
      roleMissing: false,
    };
  }

  const memberSnap = await getDoc(baseMemberDocRef(baseKey, userKey));
  if (!memberSnap.exists()) return null;

  const access = resolveMemberAccess({
    ownerKey: baseRow.ownerKey,
    userKey,
    memberRole: memberSnap.data()?.role,
    memberBaseRole: memberSnap.data()?.baseRole,
  });
  if (!access) return null;

  if (!access.isCustom) {
    return {
      ...access,
      roleDefinition: null,
      roleMissing: false,
    };
  }

  const roleSnap = await getDoc(doc(db, collections.bases, baseKey, 'roles', access.roleKey));
  if (!roleSnap.exists()) {
    return {
      ...access,
      roleDefinition: null,
      roleMissing: true,
    };
  }

  return {
    ...access,
    roleDefinition: { key: roleSnap.id, ...(roleSnap.data() || {}) },
    roleMissing: false,
  };
}

/**
 * Subscribe to effective base access for the current user.
 * callback({ roleKey, tier, isCustom, roleDefinition, roleMissing })
 */
export function subscribeToBaseAccess({ baseKey, userKey, base, callback, onError }) {
  if (!baseKey || !userKey || typeof callback !== 'function') {
    return () => {};
  }

  const baseRow = base || null;
  if (baseRow?.ownerKey === userKey) {
    callback({
      roleKey: BASE_ROLES.OWNER,
      tier: BASE_ROLES.OWNER,
      isCustom: false,
      roleDefinition: null,
      roleMissing: false,
    });
    return () => {};
  }

  let roleUnsub = null;
  let currentAccess = null;

  const emit = () => {
    if (!currentAccess) {
      callback(null);
      return;
    }
    callback(currentAccess);
  };

  const memberUnsub = onSnapshot(
    baseMemberDocRef(baseKey, userKey),
    (snap) => {
      if (roleUnsub) {
        roleUnsub();
        roleUnsub = null;
      }

      if (!snap.exists()) {
        currentAccess = null;
        emit();
        return;
      }

      const access = resolveMemberAccess({
        ownerKey: baseRow?.ownerKey,
        userKey,
        memberRole: snap.data()?.role,
        memberBaseRole: snap.data()?.baseRole,
      });

      if (!access) {
        currentAccess = null;
        emit();
        return;
      }

      if (!access.isCustom) {
        currentAccess = {
          ...access,
          roleDefinition: null,
          roleMissing: false,
        };
        emit();
        return;
      }

      roleUnsub = subscribeToBaseCustomRole(
        baseKey,
        access.roleKey,
        (roleDefinition) => {
          currentAccess = {
            ...access,
            roleDefinition,
            roleMissing: !roleDefinition,
          };
          emit();
        },
        (error) => {
          if (typeof onError === 'function') onError(error);
          currentAccess = {
            ...access,
            roleDefinition: null,
            roleMissing: true,
          };
          emit();
        }
      );
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      } else {
        console.error('[BaseRoleService] subscribeToBaseAccess error:', error);
      }
      currentAccess = null;
      emit();
    }
  );

  return () => {
    memberUnsub();
    if (roleUnsub) roleUnsub();
  };
}

/** @deprecated Prefer subscribeToBaseAccess; returns roleKey only. */
export function subscribeToBaseRole({ baseKey, userKey, base, callback, onError }) {
  return subscribeToBaseAccess({
    baseKey,
    userKey,
    base,
    callback: (access) => {
      callback(access?.roleKey || null);
    },
    onError,
  });
}

export function buildBaseMemberMap(rows = []) {
  return rows.reduce((acc, row) => {
    if (!row?.userKey) return acc;
    acc[row.userKey] = row;
    return acc;
  }, {});
}

export function getRoleFromBaseMemberMap({ base, userKey, memberByUserKey }) {
  const access = inferAccessFromRows(base, memberByUserKey?.[userKey] || null, userKey);
  return access?.roleKey || null;
}

export function getTierFromBaseMemberMap({ base, userKey, memberByUserKey }) {
  const access = inferAccessFromRows(base, memberByUserKey?.[userKey] || null, userKey);
  return access?.tier || null;
}

export function formatRoleLabel(roleKey, customRolesByKey = {}) {
  if (!roleKey) return 'Member';
  if (roleKey === BASE_ROLES.OWNER) return 'Owner';
  if (roleKey === BASE_ROLES.ADMIN) return 'Admin';
  if (roleKey === BASE_ROLES.MEMBER) return 'Member';
  const custom = customRolesByKey[roleKey];
  if (custom?.name) return custom.name;
  return roleKey;
}

export function isBaseBuiltinRole(roleKey) {
  return isBuiltinRole(roleKey);
}

export { normalizeRole, BASE_ROLES };
